import { useLingui } from '@lingui/react/macro';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DecimalInputField } from '@/components/input_field/decimal_input_field';
import { useSharedTradingStore } from '@/feature/order_book/SharedTradingStore';
import { useOrderFormCoordinator } from '../hooks/useOrderFormCoordinator';
import { useOrderFormContext } from '../order_context';
import type { OrderFormData } from '../order_schema';
import { TokenSelect } from './token_select';

interface SizeFieldProps {
  priceFieldName?: string;
}

export function SizeField({ priceFieldName }: SizeFieldProps) {
  const { t } = useLingui();
  const formContext = useFormContext<OrderFormData>();
  const { disabled, marketData } = useOrderFormContext();
  const { handleSizeChange, handleSizeUnitChange } = useOrderFormCoordinator({ priceFieldName });
  const { sizeUnit: globalSizeUnit, setSizeUnit: setGlobalSizeUnit } = useSharedTradingStore();

  const sizeUnit = formContext.watch('sizeUnit');

  useEffect(() => {
    if (globalSizeUnit !== sizeUnit) {
      formContext.setValue('sizeUnit', globalSizeUnit, { shouldValidate: false });
      handleSizeUnitChange(globalSizeUnit);
    }
  }, [globalSizeUnit]);

  const sizeTokens = useMemo(
    () => [
      {
        symbol: marketData?.symbol || 'BTC',
        decimals: marketData?.assetDecimals ?? 4,
        display_precision: marketData?.assetDecimals ?? 4,
      },
      {
        symbol: 'USD',
        decimals: 2,
        display_precision: 2,
      },
    ],
    [marketData?.symbol, marketData?.assetDecimals]
  );

  const selectedSizeToken = sizeTokens[sizeUnit === 'quote' ? 1 : 0];

  const handleTokenSelect = (token: any) => {
    const newSizeUnit = token.symbol === sizeTokens[0].symbol ? 'asset' : 'quote';
    if (sizeUnit !== newSizeUnit) {
      setGlobalSizeUnit(newSizeUnit);
    }
  };

  return (
    <Controller
      control={formContext.control}
      name="size"
      render={({ field }) => (
        <DecimalInputField
          maxPrecision={selectedSizeToken.display_precision}
          value={field.value}
          onChange={(value) => {
            field.onChange(value);
            handleSizeChange(value);
          }}
          disabled={disabled}
          label={t({ comment: 'Size field label', message: 'Size' })}
          endAdornment={<TokenSelect tokens={sizeTokens} selectedToken={selectedSizeToken} onSelectToken={handleTokenSelect} />}
        />
      )}
    />
  );
}
